import type { ReactNode } from 'react'
import { Avatar } from './Avatar'
import { Logo } from '../platform/branding/Logo'
import { useT } from '../i18n'

/** Just the fields the header needs — works for both the synthesized
 *  Profile type (App.tsx) and the raw ProfileRow from Supabase. */
interface HeaderProfile {
  display_name: string
  avatar_url: string | null
}

interface Props {
  /** Big page title rendered on the LEFT, vertically centred against the
   *  logo so it shares a row instead of pushing content down. Pass a
   *  short noun-phrase like "WEEKLY", "PROFILE", "I'D EAT THAT". */
  title?: string
  /** Tailwind text-color class for the title. Hub/Weekly/Profile use
   *  brand lime, game screens override with their game accent (e.g.
   *  text-game-iet-dark). */
  titleColorClass?: string
  /** Optional centre image. Defaults to the JDG logo. Pass `image` to
   *  swap in a per-game artwork on game-specific screens. */
  image?: string
  /** Centre image alt text — read by screen readers. */
  imageAlt?: string
  /** Right-side avatar for the user. When present, tap routes to the
   *  Profile screen via `onProfileTap`. */
  profile?: HeaderProfile | null
  onProfileTap?: () => void
  /** Custom right-cell content — used by game screens that prefer a
   *  back-arrow or close button over the avatar. Takes precedence
   *  over the avatar slot when present. */
  rightSlot?: ReactNode
  /** Custom left-cell content — overrides `title` when set. Useful for
   *  game pages that want a back button + minimal context line in the
   *  spot where Hub/Weekly/Profile show a single-word title. */
  leftSlot?: ReactNode
}

/**
 * Unified screen header — a single grid row containing:
 *   [ TITLE ]   [ centre image ]   [ avatar / custom slot ]
 *
 * Replaces the previous pattern of "centred logo above a left-aligned
 * h1" which pushed real content down by 40+ px on every page. The
 * title now sits next to the brand mark instead of below it.
 *
 * Mobile-first heights match the previous values; on tablet+ the
 * centre image grows so the wider .screen container doesn't end up
 * with a tiny mark in the middle. Tailwind `md:` breakpoints take
 * over at ≥768 px, matching .screen's max-w ramp.
 */
export function ScreenHeader({
  title,
  titleColorClass = 'text-brand-text',
  image = '/jdg-logo.webp',
  imageAlt = 'Jordan Draper Games',
  profile,
  onProfileTap,
  rightSlot,
  leftSlot,
}: Props) {
  const t = useT()
  // Use the dedicated <Logo/> for the JDG image so it stays identical
  // to other places that import it directly. For everything else,
  // render a square <img>; rounded-2xl matches the JDG stamp's outer
  // radius so game artwork doesn't read as a "loose square" in the
  // header.
  const isJdgLogo = image === '/jdg-logo.webp'


  return (
    <header className="grid grid-cols-[1fr_auto_1fr] items-center px-5 pt-0 pb-4 relative z-10">
      <div className="min-w-0">
        {leftSlot ? (
          leftSlot
        ) : title ? (
          <h1
            /* Translated titles run longer than the English one-worders
               these cells were sized for (PROFILE → プロフィール,
               SETTIMANALE), so step the size down instead of truncating.
               CJK glyphs are full-width, so weight them double. */
            className={`font-display font-black leading-none tracking-wide truncate
                        ${titleWidth(title) > 9 ? 'text-lg md:text-2xl' : 'text-2xl md:text-3xl'}
                        ${titleColorClass}`}
          >
            {title}
          </h1>
        ) : null}
      </div>

      <div className="flex justify-center">
        {isJdgLogo ? (
          <Logo size={64} />
        ) : (
          <img
            src={image}
            alt={imageAlt}
            width={64}
            height={64}
            draggable={false}
            className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover shadow-card"
          />
        )}
      </div>

      <div className="flex justify-end">
        {rightSlot ? (
          rightSlot
        ) : profile && onProfileTap ? (
          <button
            onClick={onProfileTap}
            className="active:scale-90 transition-transform"
            aria-label={t('Profile')}
          >
            <Avatar
              name={profile.display_name}
              avatarUrl={profile.avatar_url}
              size="md"
            />
          </button>
        ) : null}
      </div>
    </header>
  )
}

/**
 * Rough advance width of a title in "Latin character" units. CJK is
 * full-width, so a 6-character Japanese title takes about as much room
 * as a 12-character English one.
 */
function titleWidth(s: string): number {
  let w = 0
  for (const ch of s) w += /[\u3000-\u9fff\uff00-\uffef]/.test(ch) ? 2 : 1
  return w
}
